/**
 * Division scoping for the acting (logged-in) user.
 *
 * A Care Advisor or Collector may only create/assign patients to their OWN
 * division(s) — they must not be able to pick a division under their client
 * tag that they aren't personally assigned to. Admin / SuperAdmin /
 * OrganizationAdmin (and every other actor) keep the full option list — the
 * backend remains the source of truth and enforces the same policy.
 */
import { UserRoleName } from './types/UserProfile';

interface ActingUserLike {
  role?: { name?: string } | string | null;
  divisions?: Array<{ id: number | string }> | null;
}

function actingRoleName(user: ActingUserLike | null | undefined): string | undefined {
  if (!user) return undefined;
  return typeof user.role === 'string' ? user.role : user.role?.name;
}

function actingOwnedDivisionIds(user: ActingUserLike | null | undefined): string[] {
  return (user?.divisions || [])
    .map((d) => (d == null ? '' : String(d.id)))
    .filter((id) => id !== '' && id !== 'undefined' && id !== 'null');
}

/**
 * Restrict division options to the acting Care Advisor's/Collector's own
 * division(s). No-op for any other acting role. Also a no-op when the actor
 * has no divisions of their own, so a CA/Collector who isn't scoped to a
 * specific division keeps seeing every division under the selected client
 * tag, matching prior behavior for unscoped users.
 *
 * `preserveIds` keeps a patient's already-assigned division visible even if
 * it isn't one of the actor's own — mirrors `resolveEditableClientTags`, so
 * editing a patient never silently drops an existing out-of-scope
 * assignment from the picker; the actor still can't pick a *different*
 * division outside their own.
 */
export function scopeDivisionOptionsToActor<T extends { id?: unknown; value?: unknown }>(
  options: T[],
  actingUser: ActingUserLike | null | undefined,
  preserveIds: Array<string | number> = [],
): T[] {
  const role = actingRoleName(actingUser);
  if (role !== UserRoleName.CareAdvisor && role !== UserRoleName.Collector) {
    return options;
  }
  const ownedIds = new Set(actingOwnedDivisionIds(actingUser));
  if (ownedIds.size === 0) {
    return options;
  }
  preserveIds.forEach((id) => ownedIds.add(String(id)));
  return options.filter((option) => ownedIds.has(String(option.id ?? option.value)));
}
